import React, { useEffect, useState } from 'react';
import { useParams, useLocation, Link as RouterLink } from 'react-router-dom';
import { Container, Box, Typography, Button, Divider } from '@mui/material';
import { motion } from 'framer-motion';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { getSiteContent } from '../../services/storage';
import { DEFAULT_CONTENT, toSlug } from './UeberUnsPage';
import type { TeamMember } from './UeberUnsPage';

const MotionBox = motion(Box);

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: 'easeOut' },
  }),
};

function getInitials(name: string): string {
  return name.split(' ').map((p) => p.charAt(0)).slice(0, 2).join('').toUpperCase();
}

const SectionBlock: React.FC<{ icon: React.ReactNode; title: string; items: string[]; index: number }> = ({ icon, title, items, index }) => (
  <MotionBox
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    custom={index}
    variants={fadeUp}
    sx={{ mb: 6 }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
      <Box sx={{ color: '#1737bc' }}>{icon}</Box>
      <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#0a0a1a', letterSpacing: '-0.3px' }}>
        {title}
      </Typography>
    </Box>
    <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
      {items.map((item, i) => (
        <Box key={i} component="li" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#1737bc', mt: '3px', flexShrink: 0 }} />
          <Typography sx={{ color: '#444', fontSize: '0.95rem', lineHeight: 1.7 }}>
            {item}
          </Typography>
        </Box>
      ))}
    </Box>
  </MotionBox>
);

const TeamMemberPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [member, setMember] = useState<TeamMember | null>(
    (location.state as { member?: TeamMember })?.member ?? null
  );
  const [loading, setLoading] = useState(!member);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (member) return;
    getSiteContent('about_us')
      .then((data) => {
        const content = (data && Object.keys(data).length > 0 ? data : DEFAULT_CONTENT) as unknown as typeof DEFAULT_CONTENT;
        const found = content.team.find((m) => toSlug(m.name) === slug) ?? null;
        setMember(found ?? DEFAULT_CONTENT.team.find((m) => toSlug(m.name) === slug) ?? null);
      })
      .catch(() => {
        setMember(DEFAULT_CONTENT.team.find((m) => toSlug(m.name) === slug) ?? null);
      })
      .finally(() => setLoading(false));
  }, [slug, member]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ color: '#888' }}>Laden…</Typography>
      </Box>
    );
  }

  if (!member) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Teammitglied nicht gefunden</Typography>
        <Button component={RouterLink} to="/ueber-uns" startIcon={<ArrowBackIcon />} sx={{ color: '#1737bc' }}>
          Zurück zum Team
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '100vh' }}>

      {/* ── Hero ── */}
      <Box sx={{ bgcolor: '#0a0a1a', pt: { xs: 14, md: 16 }, pb: { xs: 8, md: 10 }, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: -120, left: -80, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(23,55,188,0.18) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <MotionBox initial="hidden" animate="visible" custom={0} variants={fadeUp}>
            <Button
              component={RouterLink}
              to="/ueber-uns"
              startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
              sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem', mb: 5, px: 0, '&:hover': { color: '#fff', bgcolor: 'transparent' } }}
            >
              Zurück zum Team
            </Button>
          </MotionBox>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 5, md: 8 }, alignItems: { md: 'flex-end' } }}>
            {/* Photo / Initials */}
            <MotionBox initial="hidden" animate="visible" custom={1} variants={fadeUp} sx={{ flexShrink: 0 }}>
              {member.image ? (
                <Box
                  component="img"
                  src={member.image}
                  alt={member.name}
                  sx={{ width: { xs: 140, md: 180 }, height: { xs: 140, md: 180 }, borderRadius: '16px', objectFit: 'cover', border: '3px solid rgba(255,255,255,0.1)', display: 'block' }}
                />
              ) : (
                <Box sx={{ width: { xs: 140, md: 180 }, height: { xs: 140, md: 180 }, borderRadius: '16px', bgcolor: '#1737bc', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '3px solid rgba(255,255,255,0.1)' }}>
                  <Typography sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' }, fontWeight: 800, color: 'rgba(255,255,255,0.25)' }}>
                    {getInitials(member.name)}
                  </Typography>
                </Box>
              )}
            </MotionBox>

            {/* Name block */}
            <Box sx={{ pb: { md: 0.5 } }}>
              <MotionBox initial="hidden" animate="visible" custom={2} variants={fadeUp}>
                <Typography sx={{ fontSize: '0.72rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: '#818cf8', fontWeight: 700, mb: 1.5 }}>
                  Unser Team
                </Typography>
              </MotionBox>
              <MotionBox initial="hidden" animate="visible" custom={3} variants={fadeUp}>
                <Typography variant="h1" sx={{ fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 800, color: '#fff', lineHeight: 1.1, mb: 1 }}>
                  {member.name}
                </Typography>
              </MotionBox>
              {member.qualification && (
                <MotionBox initial="hidden" animate="visible" custom={4} variants={fadeUp}>
                  <Typography sx={{ fontSize: '1rem', color: '#818cf8', fontWeight: 600, mb: 0.75 }}>
                    {member.qualification}
                  </Typography>
                </MotionBox>
              )}
              <MotionBox initial="hidden" animate="visible" custom={5} variants={fadeUp}>
                <Typography sx={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.5, maxWidth: 520 }}>
                  {member.role}
                </Typography>
              </MotionBox>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ── Content ── */}
      <Container maxWidth="md" sx={{ py: { xs: 7, md: 10 } }}>

        {/* Ausbildung */}
        {member.ausbildung && member.ausbildung.length > 0 && (
          <SectionBlock
            index={0}
            icon={<SchoolOutlinedIcon />}
            title="Ausbildung"
            items={member.ausbildung}
          />
        )}

        {member.ausbildung && <Divider sx={{ mb: 5 }} />}

        {/* Expertise */}
        {member.expertise && member.expertise.length > 0 && (
          <SectionBlock
            index={1}
            icon={<StarBorderIcon />}
            title="Expertise & Spezialisierung"
            items={member.expertise}
          />
        )}

        {member.expertise && <Divider sx={{ mb: 5 }} />}

        {/* Berufserfahrung */}
        {member.berufserfahrung && member.berufserfahrung.length > 0 && (
          <SectionBlock
            index={2}
            icon={<WorkOutlineIcon />}
            title="Berufserfahrung & Erfolge"
            items={member.berufserfahrung}
          />
        )}

        {/* Buttons */}
        <MotionBox
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={3}
          variants={fadeUp}
          sx={{ mt: 6, display: 'flex', gap: 2, flexWrap: 'wrap' }}
        >
          <Button
            component={RouterLink}
            to="/contact"
            variant="contained"
            sx={{ bgcolor: '#1737bc', color: '#fff', fontWeight: 700, px: 4, py: 1.5, borderRadius: '8px', '&:hover': { bgcolor: '#0f2899' } }}
          >
            Kontakt aufnehmen
          </Button>
          <Button
            component={RouterLink}
            to="/ueber-uns"
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={{ borderColor: '#1737bc', color: '#1737bc', fontWeight: 600, px: 3, py: 1.5, borderRadius: '8px', '&:hover': { bgcolor: '#1737bc', color: '#fff' } }}
          >
            Alle Teammitglieder
          </Button>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default TeamMemberPage;
